import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Globe, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LANGS = [
  { code: "de", label: "Deutsch" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Sprachwechsel mit echten URLs (/en/..., /es/..., Deutsch ohne Präfix)
  const switchLang = (code) => {
    setLang(code);
    const stripped = location.pathname.replace(/^\/(en|es)(?=\/|$)/, "") || "/";
    const target = code === "de" ? stripped : `/${code}${stripped === "/" ? "" : stripped}`;
    navigate(target);
  };

  // Alle Navigationspunkte sind echte Unterseiten (SPA-Routing, kein Reload)
  const links = [
    { to: "/fahrzeuge", label: t.nav.vehicles, key: "fahrzeuge" },
    { to: "/auto-abo", label: t.nav.abo, key: "auto-abo" },
    { to: "/mietwagen", label: t.nav.rental, key: "mietwagen" },
    { to: "/finanzierung", label: t.nav.financing, key: "finanzierung" },
    { to: "/ankauf", label: t.nav.ankaufInzahlung, key: "ankauf" },
    { to: "/leistungen", label: t.nav.services, key: "leistungen" },
    { to: "/verkaufte-fahrzeuge", label: t.nav.sold, key: "verkaufte-fahrzeuge" },
    { to: "/ueber-uns", label: t.nav.about, key: "ueber-uns" },
    { to: "/karriere", label: t.nav.career, key: "karriere" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "glass border-b border-white/10 py-3" : "py-5 bg-transparent"
      }`}
      data-testid="main-header"
    >
      <div className="max-w-[1600px] mx-auto px-4 2xl:px-10 flex items-center justify-between gap-3 2xl:gap-6">
        <Link to="/" data-testid="logo-link" className="shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <Logo compact />
        </Link>

        <nav className="hidden xl:flex items-center gap-1 2xl:gap-4 min-w-0">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className="text-[10px] tracking-normal 2xl:text-[13px] 2xl:tracking-wider text-zinc-300 hover:text-brand-bright transition-colors uppercase font-medium whitespace-nowrap"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-switcher"
                className="flex items-center gap-1.5 text-sm text-zinc-300 hover:text-brand-bright transition-colors uppercase tracking-wider border border-white/15 px-2 2xl:px-3 py-2"
              >
                <Globe className="w-4 h-4" />
                {lang}
                <ChevronDown className="w-3 h-3 hidden 2xl:inline" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-obsidian-elevated border-white/10 text-white">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => switchLang(l.code)}
                  className={`cursor-pointer focus:bg-brand focus:text-black ${
                    lang === l.code ? "text-brand-bright" : "text-zinc-300"
                  }`}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/kontakt"
            data-testid="header-contact-cta"
            className="hidden md:inline-flex bg-brand text-black font-bold uppercase tracking-wider text-sm px-3 2xl:px-5 py-2.5 hover:bg-brand-hover transition-colors whitespace-nowrap"
          >
            {t.nav.contact}
          </Link>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="xl:hidden text-white p-2" data-testid="mobile-menu-trigger" aria-label="Menu">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-obsidian border-white/10 text-white w-[280px]">
              <div className="flex flex-col gap-2 mt-10">
                <Logo className="mb-8" />
                {links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${l.key}`}
                    className="text-left text-lg text-zinc-200 hover:text-brand-bright py-3 border-b border-white/5 uppercase tracking-wider"
                  >
                    {l.label}
                  </Link>
                ))}
                <Link
                  to="/kontakt"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-contact"
                  className="text-left text-lg text-zinc-200 hover:text-brand-bright py-3 border-b border-white/5 uppercase tracking-wider"
                >
                  {t.nav.contact}
                </Link>
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="text-left text-sm text-zinc-500 hover:text-brand-bright py-3 uppercase tracking-wider"
                  data-testid="mobile-nav-admin"
                >
                  {t.nav.admin}
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
